import React, { useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { CheckboxEntity } from 'site-modules/lease-deals/components/filters/entities/options-entity';

const checkboxUpdateStrategy = (currentSelectedValues, value, isChecked) => {
  const oldValues = currentSelectedValues || [];
  if (isChecked) return [...oldValues, value];
  return oldValues.filter(v => v !== value);
};

export function CheckboxList({
  options,
  onChange,
  checkboxComponent: CheckboxComponent,
  classNames = null,
  selectedValues = undefined,
}) {
  // Performance optimization: Store selectedValues in a ref to prevent handleChange from being recreated
  // on every selectedValues change, which would cause all checkboxes to re-render unnecessarily.
  // The ref ensures handleChange has access to current values while maintaining stable reference.
  const selectedValuesRef = useRef(selectedValues);
  useEffect(() => {
    selectedValuesRef.current = selectedValues;
  }, [selectedValues]);

  const handleChange = useCallback(
    event => {
      const { value, checked } = event.target;
      const newValues = checkboxUpdateStrategy(selectedValuesRef.current, value, checked);

      onChange(newValues, value, checked);
    },
    [onChange]
  );

  return (
    <ul className={classnames('list-unstyled mb-0', classNames)}>
      {options.map(({ label, value }) => (
        <CheckboxComponent
          key={value}
          checked={selectedValues?.includes(value)}
          onChange={handleChange}
          value={value}
          label={label}
        />
      ))}
    </ul>
  );
}

CheckboxList.propTypes = {
  options: CheckboxEntity.isRequired,
  onChange: PropTypes.func.isRequired,
  checkboxComponent: PropTypes.elementType.isRequired,
  classNames: PropTypes.string,
  selectedValues: PropTypes.arrayOf(PropTypes.string),
};
